import { Injectable, PLATFORM_ID, Inject, NgZone } from '@angular/core';
import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { TransferState, makeStateKey } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
// store
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

// services
import { BookingService } from '../../booking/services/booking.service';
import { DataStorageService } from './data-storage.service';
import { EventsService } from '../../booking/services/events.service';
import { LoginService } from './login.service';
import { BagItemsService } from './bagItems.service';
// models
import { AppState } from '../../booking/models/app-state.interface';
import { initialManualFlightData } from '../../booking/models/booking-init.model';
import { refreshBookingState, resetBookingState, sbbSubsidyStatusUpdate, updateBagItems, updateBookingProduct, updateBookingReference, updateBookingType, updateJourneyLeg, updateSbbPublicTransportDiscountStatus, updateTimeslot, updateTotalPrice, updateVirtualBags } from 'app/booking/reducers/booking.actions';
import { CURRENT_BOOKING_LOADED, FLIGHT_LOADED, USER_LOADED } from '@shared/reducers/loading-status.reducer';
import { refreshBookingObject, resetBookingObject, updateBookingObject } from 'app/booking/reducers/current-booking.reducer';
import { refreshD2dBooking, resetD2dState, updateInboundBookingObject, updateOutboundBookingObject } from 'app/booking/reducers/d2d-booking.reducer';
import { panelSet } from 'app/booking/reducers/panel.reducer';
import { refreshFlightState, resetFlightState, updateFinalDestination } from 'app/booking/reducers/flight.reducer';
import { refreshFlightFinder, resetFlightFinder, resetSelectedMultiLegFlight, updateSessionUsed } from '@shared/reducers/flight-finder.action';
import { refreshTimeslotState, resetTimeslotState } from 'app/booking/reducers/timeslot.reducer';
import { refreshHubDrop, resetHubDropReducer } from 'app/booking/reducers/hubdrop.reducer';
import { refreshFlybackReducer, resetFlybackReducer } from 'app/booking/reducers/flyback.reducer';
import { refreshUpsellBooking, resetUpsellBooking } from 'app/booking/reducers/upsell-booking.reducer';
import { refreshMilesReducer, resetMiles } from 'app/booking/reducers/milesAndMore.reducer';
import { refreshUtilityReducer, resetUtilityReducer } from '@shared/reducers/utility.actions';
import { refreshOldBookingInfoReducer, resetOldBookingInfoReducer } from 'app/booking/reducers/old-booking-info.reducer';
import { REFRESH_PARTNER, RESET_PARTNER, UPDATE_PARTNER_NAME } from '@shared/reducers/partner.reducer';
import { resetPrice } from 'app/booking/reducers/price.reducer';
import { RESET_USER_STATE } from '@shared/reducers/user.reducer';
import { RESET_USER_AGENT_DATA } from '@shared/reducers/user-agent.reducer';
// reducers
// import { REFRESH_BOOKING_OBJECT, RESET_BOOKING_OBJECT, UPDATE_BOOKING_OBJECT } from '../../booking/reducers/current-booking.reducer';
// import { RESET_PRICE } from '../../booking/reducers/price.reducer';
// import { REFRESH_USER_STATE, RESET_USER_STATE } from '../../shared/reducers/user.reducer';
// import { RESET_FLIGHT_STATE } from '../../booking/reducers/flight.reducer';
// import {
//   REFRESH_BOOKING_STATE,
//   RESET_BOOKING_STATE,
//   UPDATE_BOOKING_REFERENCE,
//   UPDATE_VIRTUAL_BAGS,
//   UPDATE_TOTAL_PRICE,
//   UPDATE_JOURNEY_LEG,
//   UPDATE_BOOKING_PRODUCT,
//   UPDATE_BOOKING_TYPE
// } from '../../booking/reducers/booking.reducer';


const STORE_INIT_KEY = makeStateKey('store_init');

@Injectable({
  providedIn: 'root',
})
export class AppStateService {

  private _userSavedSubscription: Subscription;
  private _dataIncompleteSave: Subscription;
  private _dataUpgradeSave: Subscription;
  private _bookingProduct: string;
  private _storeInitUsed = false;

  public bookingProductType$: Subject<any> = new Subject();
  public userLoginValid$: Subject<any> = new Subject();
  public bookingProductUpgrade$: Subject<any> = new Subject();
  public completeBookingRefreshed$: BehaviorSubject<any> = new BehaviorSubject(null);
  public completeBookingRefreshedFromStore$: BehaviorSubject<any> = new BehaviorSubject(null);
  public storeInitComplete$: Subject<any> = new Subject();
  public userAPIResponse$: Subject<any> = new Subject();
  public flightFinderRefreshed$: BehaviorSubject<any> = new BehaviorSubject(false);
  public token;
  public bookingRef;

  constructor(
    private _appStore: Store<AppState>,
    private _bookingService: BookingService,
    private _dataStorageService: DataStorageService,
    private _loginService: LoginService,
    private _eventsService: EventsService,
    private _router: Router,
    private _serverState: TransferState,
    private _bagItemsService: BagItemsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  // get store state
  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1)).subscribe(s => state = s);
    return state;
  }

  setAppState(type?, product?) {
    this._bookingProduct = product;
    const state = this.getState(this._appStore);
    // check if cookie set
    if (!this._dataStorageService.getCookieDataID() || state.bookingReducer.bookingDetails.bookingPaid) {
      this._router.navigate(['flight-finder/search'], {queryParams: {'start-booking': true}});
      return;
    } else if (type === 'incomplete') { // TODO:
      const departureAirportCode = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0].departureAirport.airportCode;
      const isHubdropBooking = state.utilityReducer.isHubdropBooking;
      const flightStatusDetails = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
      const airlineCode = flightStatusDetails.airlineCode === 'LX' && flightStatusDetails.operatingAirlineCode === 'LX' ? 'LX' : 'None';
      this._eventsService.generatePaths(this._bookingProduct, departureAirportCode, isHubdropBooking, airlineCode);
      this.setBookingRefreshState(state);
    } else if (type === 'amend') {
      if(state.bookingReducer.bookingDetails.bookingSelectionType) {
        this._bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
        const departureAirportCode = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0].departureAirport.airportCode;
        const isHubdropBooking = state.utilityReducer.isHubdropBooking;
        const flightStatusDetails = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
        const airlineCode = flightStatusDetails.airlineCode === 'LX' && flightStatusDetails.operatingAirlineCode === 'LX' ? 'LX' : 'None';
        this._eventsService.generatePaths(this._bookingProduct, departureAirportCode, isHubdropBooking, airlineCode);
      } else {
        // check booking product type
        const isHubdropBooking = state.utilityReducer.isHubdropBooking;
        this._eventsService.generatePathsForAmend(
          this._bookingProduct, this._eventsService.setPathsForAmends(state.bookingReducer.bookingDetails.bookingAmendSection, isHubdropBooking)
        );
      }

      this.setBookingRefreshState(state);
    } else if (state.loadedStatusReducer.flightLoaded) {
      // store already loaded - coming from flight finder
      // refresh from loaded store
      this.getBookingStateFromStore();
    } else if (type === 'new' || type === 'refresh') {
      // get stored data from API
      // not coming from flight finder
      this.getBookingStateFromAPI();
    }
  }

  // set init values in store
  setStoreStateInit(checkUser?) {

    const storeInit = this._serverState.get(STORE_INIT_KEY, null as any);
    if (!storeInit && !this._storeInitUsed) {
      this._dataStorageService.getSavedDataPromise().then((res: AppState) => {
        this._serverState.set(STORE_INIT_KEY, res as any);
        this.setStoreItems(res, isPlatformBrowser(this.platformId) ? true : false);
      });
    } else if (this._storeInitUsed) {
      this._dataStorageService.getSavedDataPromise(true).then((res: AppState) => {
        this.setStoreItems(storeInit, checkUser);
      });
    } else {
      this.setStoreItems(storeInit, checkUser);
      this._storeInitUsed = true;
    }

  }

  setStoreItems(s: any, checkUser: boolean) {
    // // console.log('store-reset',s);
    this._appStore.dispatch(CURRENT_BOOKING_LOADED({ currentBookingLoaded: false }));

    this.resetFlightData();
    this.resetSelectedMultilegFlight();
    this.resetCurrentBookingData();
    this.resetBookingStateData();
    this.resetPriceData();
    this.resetTimeslotData();
    this.resetHubDropReducer();
    // this.resetMilesData();
    // this.resetD2dBookingData();
    // this.resetUserData();
    // this.resetUserAgentData();
    this._appStore.dispatch(panelSet({payload: 1 }));

    if (checkUser) {
      this.checkUserLogin();
    } else {
      this.resetUserData();
    }

    this.storeInitComplete$.next(true);

  }
  // called on refresh
  setStoreRefreshState(data, isComplete?, amendFlight?) {
    this._appStore.dispatch(refreshBookingState({payload: data.bookingReducer }));
    this._appStore.dispatch(refreshBookingObject({ payload: data.currentBookingReducer }));
    this._appStore.dispatch(refreshFlightState({ payload: data.flightReducer }));
    if(!amendFlight) {
      this._appStore.dispatch(refreshFlightFinder({ payload: data.flightFinderReducer }));
    }
    this._appStore.dispatch(refreshTimeslotState({ state: data.timeslotReducer }));
    this._appStore.dispatch(FLIGHT_LOADED({ flightLoaded: true }));
    this._appStore.dispatch(refreshD2dBooking({payload: data.d2dReducer }));
    this._appStore.dispatch(refreshHubDrop({ payload: data.hubDropReducer }));
    this._appStore.dispatch(refreshFlybackReducer({ payload: data.flybackReducer }));
    this._appStore.dispatch(refreshUpsellBooking({ state: data.upsellBookingReducer }));
    this._appStore.dispatch(refreshMilesReducer({ payload: data.milesAndMoreReducer }));
    this._appStore.dispatch(refreshUtilityReducer({ payload: data.utilityReducer }));
    this._appStore.dispatch(refreshOldBookingInfoReducer({ payload: data.oldBookingInfoReducer }));

    if (data.bookingReducer.bookingDetails.bookingType !== 'amend') {
      this._appStore.dispatch(panelSet({ payload: data.updatePanelReducer }));
    } else {
      this._appStore.dispatch(panelSet({ payload: 1 }));
    }

    this._dataStorageService.saveData();
  }

  setBookingStores(data) {
    this._appStore.dispatch(refreshBookingObject({ payload: data.currentBookingReducer }));
    this._appStore.dispatch(refreshBookingState({ payload: data.bookingReducer }));
    this._appStore.dispatch(refreshTimeslotState({ state: data.timeslotReducer }));
    this._appStore.dispatch(refreshHubDrop({ payload: data.hubDropReducer }));
    // this._appStore.dispatch(refreshFlybackReducer({ payload: data.flybackReducer }));
    this._appStore.dispatch(refreshOldBookingInfoReducer({ payload: data.oldBookingInfoReducer }));
    this._appStore.dispatch(refreshFlightState({payload: data.flightReducer }));
    this._appStore.dispatch(FLIGHT_LOADED({ flightLoaded: true }));
  }

  refreshPartner(data) {

    // set all saved partners to NONE
    // remove if using partners again
    data.partnerReducer.partnerName = 'NONE';

    if (typeof data.partnerReducer === 'undefined') {
      this._appStore.dispatch(RESET_PARTNER());
    } else {
      if (data.partnerReducer.partnerName === 'None') { data.partnerReducer.partnerName = 'NONE'; }
      this._appStore.dispatch(REFRESH_PARTNER({partnerDetails: data.partnerReducer}));
    }
  }

  refreshFlightQuoteState(count, page) {

    let callCount = 0;
    const flightFinderStore = this._dataStorageService.getState(this._appStore).flightFinderReducer;
    if (page === 'results' && (flightFinderStore.quoteFlightResults === null || flightFinderStore.quoteFlightResults.length === 0)) {
      this._dataStorageService.getSavedDataPromise(true).then((store: AppState) => {
        // store.flightFinderReducer.requestCount = 0;
        this.refreshFlightFinder(callCount, count, store.flightFinderReducer);
        callCount++;
      });
    } else {
      this.refreshFlightFinder(callCount, count, flightFinderStore);
      callCount++;
      return;
    }
  }

  refreshFlightFinder(callCount, count, store) {
    if (callCount !== count) {
      this._appStore.dispatch(refreshFlightFinder({  payload: store }));
      this.flightFinderRefreshed$.next(true);
    }
  }

  amendFlightDestination(data) {

    if(data.currentBookingReducer.currentBooking.makePaymentRequest.sbbSubsidyAmount > 0) {
      this._appStore.dispatch(updateSbbPublicTransportDiscountStatus({ payload: true }));
      this._appStore.dispatch(sbbSubsidyStatusUpdate({ payload: true }));
      // console.log('ssb applied');
    }

    if (data.currentBookingReducer.currentBooking.bookingJourney[0].flightData === null) {

      this._appStore.dispatch(updateFinalDestination({
        payload: {
          airportCode: '',
          airportName: data.flightReducer.flightDetails.flightManualDestination.airportName,
          airportCityName: '',
          airportCountryName: ''
        }
      }));

    } else {

      const flightLeg = data.currentBookingReducer.currentBooking.bookingJourney[0].flightData.flightLeg;
      if (flightLeg === 1 || flightLeg === 2) {

        this._appStore.dispatch(updateFinalDestination({
          payload: {
              airportCode: data.flightReducer.flightDetails.flightResponse.flightStatusDetails[flightLeg - 1].arrivalAirport.airportCode,
              airportName: data.flightReducer.flightDetails.flightResponse.flightStatusDetails[flightLeg - 1].arrivalAirport.airportName,
              airportCityName:
              data.flightReducer.flightDetails.flightResponse.flightStatusDetails[flightLeg - 1].arrivalAirport.airportCityName,
              airportCountryName:
              data.flightReducer.flightDetails.flightResponse.flightStatusDetails[flightLeg - 1].arrivalAirport.airportCountryName
          }
        }));
      } else if (flightLeg === 99) {
        this._appStore.dispatch(updateFinalDestination({
          payload: {
              airportCode: data.flightReducer.flightDetails.flightFinalDestination.airportCode,
              airportName: data.flightReducer.flightDetails.flightFinalDestination.airportName,
              airportCityName: data.flightReducer.flightDetails.flightFinalDestination.airportCityName,
              airportCountryName: ''
          }
        }));
      }

    }
  }

  setBookingRefreshState(data) {

    let journeyType = 1;
    let journeyTypeString = 'abc';
    if (data.currentBookingReducer.currentBooking.bookingJourney[0].addOnProductType.length === 0) {
      journeyType = 0;
      journeyTypeString = 'ccd';
    }
    const journeyDirection = data.currentBookingReducer.currentBooking.bookingJourney[0].journeyDirection;
    this.setJourneyDetails(1, journeyType, journeyDirection);

    if (data.bookingReducer.bookingDetails.virtualBagCount === 0 && data.currentBookingReducer.currentBooking.bookingJourney[0].virtualBagCount === 0) {
      this.setVirtualBags(1);
      this.setBagItems({ bags: 1, childEquipment: 0, oversizeItem: 0 });
    } else {
      this.setVirtualBags(data.currentBookingReducer.currentBooking.bookingJourney[0].virtualBagCount);
      // set virtualBagsClassification
      this.setBagItems(this._bagItemsService.BagsClassificationToBagItems(data.currentBookingReducer.currentBooking.bookingJourney[0].virtualBagClassification))
    }

    // bagsClassification?

    this.setPrice('0');

    if (data.bookingReducer.bookingDetails.bookingType === 'amend') {
      this.amendFlightDestination(data);
    }

    this._appStore.dispatch(updateBookingReference({payload: data.currentBookingReducer.currentBooking.bookingReference}));
    this._appStore.dispatch(updateBookingObject({ payload: data.currentBookingReducer.currentBooking }));
    if(journeyDirection === 1) {
          this._appStore.dispatch(updateOutboundBookingObject({ payload: data.currentBookingReducer.currentBooking}));
        } else {
          this._appStore.dispatch(updateInboundBookingObject({ payload: data.currentBookingReducer.currentBooking}));
        }
        this._appStore.dispatch(panelSet({ payload: 1 }));

    this._dataStorageService.saveData();
    this._dataIncompleteSave = this._dataStorageService.dataSaved$
      .subscribe(res => {
        if (res) {
          this._bookingService.createBookingFromStore(data.currentBookingReducer);
          this._dataIncompleteSave.unsubscribe();
        }
      });
  }

  getBookingStateFromStore(createBooking?: Boolean, isComplete?: Boolean) {
    const res = this.getState(this._appStore);
    this.checkStateItems(res, isComplete, undefined);
  }

  // HTTP CALL TO GET INCOMPLETE - hydrate store with returned data
  // create booking - used on booking form
  getBookingStateFromAPI(createBooking?: Boolean, isComplete?: Boolean) {
    this._dataStorageService.getSavedDataPromise(true).then((res: AppState) => {
      this.checkStateItems(res, isComplete, undefined);
    });
  }

  // check state items
  // checks if booking is complete, user state and booking data
  // refreshes store if required and generates panel path array
  checkStateItems(res, isComplete?, amendFlight?) {
    // // console.log('store', res, isComplete);
    if (isComplete) {
      this.setStoreRefreshState(res, isComplete, amendFlight);
      // if user has logged in
      // check token is still valid
      if (res.userReducer === null) {
        this.checkUserLogin();
      }

      this.completeBookingRefreshed$.next(true);

      return;
    }

    // is booking paid?
    if (res.bookingReducer.bookingDetails.bookingPaid) {
      // yes - redirect
      this._router.navigate(['flight-finder/search'], {queryParams: {'start-booking': true}});
    } else {

      // no - refresh data
      // set paths for form if amend
      if (res.bookingReducer.bookingDetails.bookingType === 'amend') {
        // check booking product type
        if(!amendFlight) {
          const isHubdropBooking = this.getState(this._appStore).utilityReducer.isHubdropBooking;
          this._eventsService.generatePathsForAmend(
            this._bookingProduct, this._eventsService.setPathsForAmends(res.bookingReducer.bookingDetails.bookingAmendSection, isHubdropBooking)
          );
        }else {
          this._bookingProduct = res.bookingReducer.bookingDetails.bookingProduct;
          const departureAirportCode = res.flightReducer.flightDetails.flightResponse.flightStatusDetails[0].departureAirport.airportCode;
          const isHubdropBooking = res.utilityReducer.isHubdropBooking;
          const flightStatusDetails = res.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
          const airlineCode = flightStatusDetails.airlineCode === 'LX' && flightStatusDetails.operatingAirlineCode === 'LX' ? 'LX' : 'None';
          this._eventsService.generatePaths(this._bookingProduct, departureAirportCode, isHubdropBooking, airlineCode);
        }
      } else {
        const departureAirportCode = res.flightReducer.flightDetails.flightResponse?.flightStatusDetails[0].departureAirport.airportCode;
        const isHubdropBooking = res.hubDropReducer.selectedPostOffice !== null && res.hubDropReducer.selectedCollectionType === 'atPostOffice';
        const flightStatusDetails = res.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
        const airlineCode = flightStatusDetails.airlineCode === 'LX' && flightStatusDetails.operatingAirlineCode === 'LX' ? 'LX' : 'None';
        this._eventsService.generatePaths(this._bookingProduct, departureAirportCode, isHubdropBooking, airlineCode);
      }

      if (res.bookingReducer.bookingDetails.journeyLegDetails.length === 0) {
        this._router.navigate(['flight-finder/search'], {queryParams: {'start-booking': true}});
        return;
      }

      // if user has logged in
      // check token is still valid
      if (res.userReducer === null) {
        this.checkUserLogin();
      }

      // contiune refresh after user has been checked
      this.setStoreRefreshState(res, undefined, amendFlight);
      // check if booking object is empty
      // either create new or use data from api
      if (Object.keys(res.currentBookingReducer.currentBooking).length === 0) {
        this._bookingService.createBookingInit(this._bookingProduct);
      } else {
        this._bookingService.createBookingFromStore(res.currentBookingReducer);
      }

    }
  }

  checkUserLogin(userStoreData?, returnError?) {

    let hasError = false;

    const cookieUserToken = this._dataStorageService.checkCookie('dataT');
    if (!cookieUserToken) {
      this._appStore.dispatch(USER_LOADED({ userLoaded: true }));
      return hasError = true;
    }

    this._loginService.loginUser(false, false, cookieUserToken);

    this._userSavedSubscription = this._loginService.savedUserSubject$
      .subscribe(userValid => {
        if (userValid) {
          this.userLoginValid$.next(true);
        } else {
          this.userLoginValid$.next(false);
        }

        this.userAPIResponse$.next(true);
        this._userSavedSubscription.unsubscribe();

      });
  }

  upgradeProductToABC() {
    this._appStore.dispatch(updateBookingProduct({ payload: 'abc' }));
    this.setJourneyDetails(1, 1, 1);
    this._bookingService.updateBookingProductToABC();

    this._dataStorageService.saveData();
    this._dataUpgradeSave = this._dataStorageService.dataSaved$
      .subscribe(res => {
        if (res) {
          this.bookingProductUpgrade$.next(true);
          this._dataUpgradeSave.unsubscribe();
        }
      });

  }

  // set journey type and journey direction
  // journeyID = bookingJourney leg index
  // journeyType: 1 = ABC, 2 = CCD, 0 = SDS
  // journeyDirection: GeneralLocationToAirport = 1, AirportToGeneralLocation = 2, AirportToAirport = 3
  setJourneyDetails(id, type, direction) {
    this._appStore.dispatch(updateJourneyLeg({ payload: { journeyId: id, journeyType: type, journeyDirection: direction } }));
  };
  getProductTypeAsNumber(val) {
    let newVal = 0;
    if (val === 'abc') {
      newVal = 1;
    } else if (val === 'ccd') {
      newVal = 0;
    }
    return newVal;
  }
  getDirectionAsNumber(val) {
    let newVal = 1;
    if (val === 'outbound') {
      newVal = 1;
    } else if (val === 'inbound') {
      newVal = 2;
    }
    return newVal;
  }
  updateJourneyDetails(data) {
    const journeyTypeValue = this.getProductTypeAsNumber(data.productType);
    this.setJourneyDetails(1, journeyTypeValue, data.direction);
  }

  // helper methods to call the store with updates
  resetFlightData() {
    this._appStore.dispatch(resetFlightState());
  };
  resetD2dBookingData() {
    this._appStore.dispatch(resetD2dState());
  }
  resetFlightFinderData() {
    this._appStore.dispatch(resetFlightFinder());
    this._appStore.dispatch(updateSessionUsed({ payload: true }));
  };
  resetSelectedMultilegFlight() {
    this._appStore.dispatch(resetSelectedMultiLegFlight());
  }
  resetCurrentBookingData() {
    this._appStore.dispatch(resetBookingObject());
  };
  resetBookingStateData() {
    this._appStore.dispatch(resetBookingState());
  };
  resetPriceData() {
    this._appStore.dispatch(resetPrice());
  };
  resetTimeslotData() {
    this._appStore.dispatch(resetTimeslotState());
  };
  resetUserData() {
    this._appStore.dispatch(RESET_USER_STATE());
  }
  resetPanelNumber() {
    this._appStore.dispatch(panelSet({ payload: 1 }));
  }
  resetPartner() {
    this._appStore.dispatch(UPDATE_PARTNER_NAME({  partnerName: 'NONE' }));
  }
  resetUserAgentData() {
    this._appStore.dispatch(RESET_USER_AGENT_DATA());
  }
  resetMilesData() {
    this._appStore.dispatch(resetMiles());
  }
  resetUtilityData() {
    this._appStore.dispatch(resetUtilityReducer());
  }
  resetHubDropReducer() {
    this._appStore.dispatch(resetHubDropReducer());
  }
  resetFlybackReducer() {
    this._appStore.dispatch(resetFlybackReducer());
  }
  resetOldBookingInfoReducer() {
    this._appStore.dispatch(resetOldBookingInfoReducer());
  }
  resetUpsellbookingReducer() {
    this._appStore.dispatch(resetUpsellBooking());
  }


  setVirtualBags(virtualBagCount) {
    this._appStore.dispatch(updateVirtualBags({ payload: parseInt(virtualBagCount, 10) }));
  };

  setBagItems(bagsItemsObj) {
    this._appStore.dispatch(updateBagItems({ payload: bagsItemsObj }));
  };

  setPrice(price) {
    this._appStore.dispatch(updateTotalPrice({ payload: price }));
  };
  setBookingType(type) {
    this._appStore.dispatch(updateBookingType({ payload: type }));
  }
  setBookingProduct(product) {
    this.bookingProductType$.next(product);
    this._appStore.dispatch(updateBookingProduct({ payload: product }));
  }
  setFlightFinalDestination(airportCode, airportName, airportCityName) {
    this._appStore.dispatch(updateFinalDestination({
      payload: { airportCode: airportCode, airportName: airportName, airportCityName: airportCityName }
    }));
  };
  resetAllFlightData() {
    this._appStore.dispatch(resetFlightState());
    this._appStore.dispatch(updateTimeslot({
      payload: {
        start: '',
        end: '',
        date: '',
        id: '',
        airportTimeslotsWarning: false,
        overrideTimeSlotCapacity: false
      }
    }));

    this._bookingService.updateBookingFlight({});
    this._bookingService.updateManualFlightData(initialManualFlightData);
    // this._dataStorageService.saveData();
  }

  // setApiHeader(token) {
  //   const headers = new Headers();
  //   // console.log(this.createAuthorizationHeader(headers, token));
  //   return this.createAuthorizationHeader(headers, token);
  // }
  // getApiHeader() {
  //   return this.header;
  // }

  // createAuthorizationHeader(headers: Headers, token) {
  //   headers.append('Authorization', 'bearer ' + token);
  // }



};
